/* eslint-disable @typescript-eslint/indent */
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Route, useHistory, Switch, useLocation, Redirect } from 'react-router-dom';
import { Security, SecureRoute, useOktaAuth } from '@okta/okta-react';
import { SubApp, AmplifyPoco } from '@amplify/amplify-sdk';
import { Col, Container, Row } from 'reactstrap';
import config from '../Config';
import oktaConfig from '../okta-config';
import AppDetails from './AppDetails';
import AdminLayout from './Shared/AdminLayout';
import { loadAppConfigs, onAmplifyApps, setOktaToken, urlHistory } from '../services/SubAppLoader';
import { Versions } from './Shared/Versions';
import AccessDenied from './Shared/AccessDenied';
import OAuthCallback from './Login/OAuthCallback';
import { Loading } from './Shared/Loading';
import { JwtToken } from '../services/Types.js';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import axios from 'axios';
import { ensureStorageAccess } from '../services/SafariStorageAccess';

const { REACT_APP_STAGE, REACT_APP_CDN_HOST } = process.env;

function HasAccessToRouter() {
  const { authState, authService, oktaAuth } = useOktaAuth();
  const history = useHistory();
  const location = useLocation();
  const [subApps, setSubApps] = useState<SubApp[]>([]);
  const [admin, setAdmin] = useState(false);
  const [token, setToken] = useState<string>();
  const [subAppsLoaded, setSubAppsLoaded] = useState(false);
  const [authenticationComplete, setAuthenticationComplete] = useState(false);

  // Request storage access for Safari on mount
  useEffect(() => {
    ensureStorageAccess().then(hasAccess => {
      if (!hasAccess) {
        console.warn('Storage access denied. Some features may not work correctly on Safari.');
      }
    });
  }, []);

  useEffect(() => {
    const $subApps = onAmplifyApps().subscribe(items => {
      setSubApps([...items]);
      setSubAppsLoaded(true);
    });
    return () => {
      $subApps.unsubscribe();
    };
  }, []);

  const storeIntendedRoute = () => {
    const existingPath = localStorage.getItem('intendedRoute');
    if (history.location.pathname !== '/implicit/callback' && history.location.pathname !== existingPath) {
      localStorage.setItem('intendedRoute', history.location.pathname);
    }
  };

  const restoreIntendedRoute = () => {
    const intendedRoute = localStorage.getItem('intendedRoute') || '/';
    if (intendedRoute) {
      history.push(intendedRoute);
    }
  };

  useEffect(() => {
    storeIntendedRoute();
  }, [history.location.pathname]);

  useEffect(() => {
    const oauthErrorOccurred = sessionStorage.getItem('oauth_error_occurred');

    if (oauthErrorOccurred === 'true') {
      setAuthenticationComplete(true);
      return;
    }

    if (authState?.isAuthenticated && authState?.accessToken) {
      sessionStorage.removeItem('oauth_error_occurred');
      sessionStorage.removeItem('oauth_error_type');
      sessionStorage.removeItem('oauth_error_time');

      const isLoggedIn = window.__IS_LOGGED_IN__;
      const tokenValue = authState.accessToken.accessToken;
      setOktaToken(tokenValue);
      setToken(tokenValue);
      setAuthenticationComplete(true);

      if (!isLoggedIn) {
        window.__IS_LOGGED_IN__ = true;
        if (window.amplify?.axios) {
          window.amplify.axios.post('/reporting/logins');
        }
      }

      try {
        const jwt: JwtToken = JSON.parse(atob(tokenValue.split('.')[1]));
        const adm = jwt?.Roles?.some(r => r.toLowerCase().endsWith('-admin'));
        if (adm) setAdmin(adm);
      } catch (e) {
        console.error('Failed to parse access token JWT', e);
      }
    } else {
      oktaAuth.tokenManager.get('accessToken').then((oktaToken: any) => {
        // eslint-disable-next-line no-debugger
        if (!oktaToken && !token && history.location.pathname !== '/implicit/callback' && history.location.pathname !== '/login' && history.location.pathname !== '/apps/login') {
          const baseURL = `${window.location.origin}/${AppDetails.path}`;
          let replaceUrl = toRelativeUrl(window.location.href || '/', baseURL);
          let matchTextUrl: RegExpMatchArray | null = replaceUrl.match(/amplify-portal-client/g);
          if (replaceUrl && matchTextUrl != null && matchTextUrl?.length > 1) {
            replaceUrl = replaceUrl.replace('amplify-portal-client/', '');
          }
          oktaAuth.setOriginalUri(replaceUrl);
          oktaAuth.signInWithRedirect();
        } else if (oktaToken === undefined) {
          if (history.location.pathname === '/implicit/callback') {
            oktaAuth.tokenManager.renew('accessToken');
            restoreIntendedRoute();
          } else if (history.location.pathname !== '/login' && history.location.pathname !== '/apps/login') {
            oktaAuth.signInWithRedirect();
          }
          setAuthenticationComplete(false);
        } else if (oktaToken && !token) {
          const tokenValue = oktaToken.accessToken;
          if (tokenValue) {
            setToken(tokenValue);
            try {
              const jwt: JwtToken = JSON.parse(atob(tokenValue.split('.')[1]));
              const adm = jwt?.Roles?.some(r => r.toLowerCase().endsWith('-admin'));
              if (adm) setAdmin(adm);
              if (jwt) {
                window.__IS_LOGGED_IN__ = true;
                setOktaToken(tokenValue);
                setAuthenticationComplete(true);
                restoreIntendedRoute();
              }
            } catch (e) {
              console.error('Failed to parse access token JWT', e);
            }
          }
        }
      });
    }
  }, [authState?.isAuthenticated, authState?.accessToken, authState, authService, oktaAuth, token, history.location.pathname]);

  useEffect(() => {
    const token = authState?.accessToken?.accessToken || '';
    if (token) {
      if (!window.amplify.axios) {
        let defaultOptions = {
          baseURL: `${config.amplifyApiHost}/v1/`,
          headers: {
            Accept: 'text/plain',
            'Content-Type': 'application/json-patch+json',
            client_id: `${config.amplifyClientId}`,
          },
        };

        window.amplify.axios = axios.create(defaultOptions) as any;
        window.amplify.token = token;
        try {
          const jwt: JwtToken = JSON.parse(atob(token.split('.')[1]));
          window.amplify.roles = jwt.Roles;
        } catch {
          /* empty */
        }
      }
      window.amplify.axios
        .get<AmplifyPoco[]>('/apps', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(
          (result: any) => {
            loadAppConfigs(result.data);
          },
          (error: any) => {
            console.log(error);
            window.amplify.urlHistory.push('/login');
          }
        );
    }
  }, [authState?.accessToken?.accessToken]);

  function NoMatch() {
    const location = useLocation();

    console.log(`No match for ${location.pathname}`);

    // Don't render anything if authentication is still in progress or apps aren't loaded
    if (!authenticationComplete || !subAppsLoaded) {
      return <></>;
    }

    // Handle gifts-that-give app - requires specific role check
    if (location.pathname.startsWith('/apps/sync-gifts-that-give')) {
      const userRoles = window.amplify?.roles || [];
      const hasRequiredRole = userRoles.some((role: string) => {
        const roleLower = role.toLowerCase();
        return roleLower === 'amplify-user' ||
               roleLower === 'amplify-giftsthatgive-user' ||
               roleLower === 'amplify-giftsthatgive-admin';
      });

      return hasRequiredRole ? <></> : <AccessDenied />;
    }

    // For other app paths, SecureRoute will handle access control
    // If we reach NoMatch, user either lacks access or app isn't configured
    if (location.pathname.startsWith('/apps/') && location.pathname !== '/apps/login') {
      return <></>;
    }

    // Redirect to home for any other unmatched routes once subApps are loaded
    return subApps.length > 0 ? <Redirect to="/" /> : <></>;
  }

  function getConfig() {
    return (
      <div>
        <span>Stage : {REACT_APP_STAGE} </span>
        <br />
        <span>CDN Host : {REACT_APP_CDN_HOST} </span>
        <br />
        <span>Amplify Api Host : {config.amplifyApiHost} </span>
        <br />
        <span>Amplify Client Id : {config.amplifyClientId} </span>
        <br />
        <span>Okta Client Id : {oktaConfig.oidc.clientId} </span>
        <br />
        <span>Okta Issuer : {oktaConfig.oidc.issuer} </span>
        <br />
        <br />
        <span>Okta Redirect Uri : {oktaConfig.oidc.redirectUri} </span>
        <br />
      </div>
    );
  }
  const getLogin = () => <Redirect to="/" />;

  let containerStyle: any = {};

  return (
    <Switch>
      <Route path="/version" exact component={Versions} />
      <Route path="/config" exact component={getConfig} />
      <Route path="/implicit/callback" component={OAuthCallback} />
      <Route path="/login" component={getLogin} />
      <Route path="/apps/login" component={getLogin} />

      <Route>
        {!authenticationComplete ? (
          <Loading />
        ) : (
          <AdminLayout admin={admin} subApps={subApps}>
            <Container fluid className="pt-3" style={containerStyle}>
              <Row>
                <Col>
                  <Route exact path="/">
                    <Redirect to="/apps/dashboard" />
                  </Route>
                  {subApps.map((subApp: SubApp, index: number) => (
                    <SecureRoute key={index} path={subApp.path} component={subApp.subApp} />
                  ))}
                  <Route path="*">
                    <NoMatch />
                  </Route>
                </Col>
              </Row>
            </Container>
          </AdminLayout>
        )}
      </Route>
    </Switch>
  );
}
const oktaAuth = new OktaAuth(oktaConfig.oidc);

export function SecurityGuard() {
  const history = useHistory();
  urlHistory.setHistoryInstance(history);

  const restoreOriginalUri = useCallback((oktaAuth: OktaAuth, originalUri: string) => {
    history.replace(originalUri);
  }, []);

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <HasAccessToRouter />
    </Security>
  );
}

export default function App() {
  return (
    <BrowserRouter basename={`/${AppDetails.path}`}>
      <SecurityGuard />
    </BrowserRouter>
  );
}
